import React, { useState, useEffect, useRef } from 'react';
import Footer from '../containers/footer';
import Header from '../containers/header';
import Loading from '../components/loading/loading.js';
import { getUsers, deleteUser } from '../services/users.js';
import DeleteConfirmModal from '../components/modals/DeleteConfirmModal';
import NewUserModal from '../components/modals/NewUserModal';
import EditUserModal from '../components/modals/EditUserModal';
import { Box, Chip, IconButton, Pagination, Tab, Tabs, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import PremiumIcon from '../assets/images/icons/PremiumIcon.svg';
import { useAlert } from '../context';
import { AlertError, AlertSuccess } from '../components/Alerts';

const paddingSx = {
  padding: '0 160px',
  '@media (max-width: 1280px)': { padding: '0 80px' },
  '@media (max-width: 1024px)': { padding: '0 20px' },
};

const PAGE_SIZE = 15;

const formatDate = dateStr => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const UserRow = ({ user, onClick }) => (
  <Box
    onClick={() => onClick(user)}
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 20px', marginBottom: '8px', borderRadius: '10px',
      border: '1px solid #e0e0e0', backgroundColor: '#fff',
      cursor: 'pointer', transition: 'box-shadow 0.15s',
      '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
    }}
  >
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#3E3D3D' }}>
          {user.name}
        </Typography>
        {user.company && (
          <Chip
            label={user.company}
            size="small"
            sx={{ backgroundColor: '#f0f4f8', color: '#555', fontSize: '11px', fontWeight: 500, height: '20px' }}
          />
        )}
      </Box>
      <Typography sx={{ fontSize: '13px', color: '#9e9e9e', mt: '2px' }}>
        {user.email}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
      <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
        <Typography sx={{ fontSize: '11px', color: '#bdbdbd' }}>
          Criado em {formatDate(user.created_at)}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: '#bdbdbd' }}>
          Último acesso {formatDate(user.last_sign_in_at)}
        </Typography>
      </Box>
      {user.premium && <img src={PremiumIcon} alt="premium" style={{ width: '16px' }} />}
      {user.blocked && (
        <Chip label="Bloqueado" size="small" sx={{ backgroundColor: '#c62828', color: '#fff', fontSize: '11px', fontWeight: 600, display: { xs: 'none', sm: 'flex' } }} />
      )}
    </Box>
  </Box>
);

const FilterPanel = ({ users, filters, setFilters, onClose }) => {
  const companies = [...new Set(users.map(u => u.company).filter(Boolean))].sort();
  const cities = [...new Set(users.map(u => u.city).filter(Boolean))].sort();
  const positions = [...new Set(users.map(u => u.position).filter(Boolean))].sort();
  const concessionTypes = [...new Set(users.map(u => u.concession_type).filter(Boolean))].sort();

  const FilterSelect = ({ label, field, options, searchable }) => {
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const filtered = searchable ? options.filter(o => o.toLowerCase().includes(q.toLowerCase())) : options;
    const selected = filters[field] || '';

    useEffect(() => {
      const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    if (!searchable) return (
      <Box sx={{ mb: '12px' }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', mb: '6px' }}>{label}</Typography>
        <select
          value={selected}
          onChange={e => setFilters(prev => ({ ...prev, [field]: e.target.value }))}
          style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #e0e0e0', fontSize: '13px', color: '#3E3D3D', outline: 'none', backgroundColor: '#fff' }}
        >
          <option value="">Todos</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </Box>
    );

    return (
      <Box sx={{ mb: '12px', position: 'relative' }} ref={ref}>
        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', mb: '6px' }}>{label}</Typography>
        <Box
          onClick={() => setOpen(v => !v)}
          sx={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #e0e0e0', fontSize: '13px', color: selected ? '#3E3D3D' : '#9e9e9e', backgroundColor: '#fff', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span>{selected || 'Todos'}</span>
          <span style={{ fontSize: '10px', color: '#9e9e9e' }}>▾</span>
        </Box>
        {open && (
          <Box sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', mt: '2px' }}>
            <Box sx={{ padding: '6px' }}>
              <input
                autoFocus
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Buscar..."
                onClick={e => e.stopPropagation()}
                style={{ width: '100%', padding: '5px 8px', borderRadius: '4px', border: '1px solid #e0e0e0', fontSize: '12px', outline: 'none' }}
              />
            </Box>
            <Box sx={{ maxHeight: '150px', overflowY: 'auto' }}>
              <Box
                onClick={() => { setFilters(prev => ({ ...prev, [field]: '' })); setOpen(false); setQ(''); }}
                sx={{ padding: '7px 10px', fontSize: '13px', color: '#9e9e9e', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
              >
                Todos
              </Box>
              {filtered.map(o => (
                <Box
                  key={o}
                  onClick={() => { setFilters(prev => ({ ...prev, [field]: o })); setOpen(false); setQ(''); }}
                  sx={{ padding: '7px 10px', fontSize: '13px', color: '#3E3D3D', cursor: 'pointer', backgroundColor: selected === o ? '#e8f4fb' : 'transparent', fontWeight: selected === o ? 600 : 400, '&:hover': { backgroundColor: '#f5f5f5' } }}
                >
                  {o}
                </Box>
              ))}
              {filtered.length === 0 && (
                <Box sx={{ padding: '7px 10px', fontSize: '12px', color: '#bdbdbd' }}>Nenhum resultado</Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const Toggle = ({ label, field }) => (
    <Box
      onClick={() => setFilters(prev => ({ ...prev, [field]: !prev[field] }))}
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 0', cursor: 'pointer', mb: '4px',
      }}
    >
      <Typography sx={{ fontSize: '13px', color: '#3E3D3D' }}>{label}</Typography>
      <Box sx={{
        width: '36px', height: '20px', borderRadius: '10px',
        backgroundColor: filters[field] ? '#006494' : '#e0e0e0',
        position: 'relative', transition: 'background 0.2s',
      }}>
        <Box sx={{
          position: 'absolute', top: '3px',
          left: filters[field] ? '18px' : '3px',
          width: '14px', height: '14px',
          borderRadius: '50%', backgroundColor: '#fff',
          transition: 'left 0.2s',
        }} />
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      position: 'absolute', top: '44px', right: 0, zIndex: 100,
      width: '260px', backgroundColor: '#fff',
      borderRadius: '10px', border: '1px solid #e0e0e0',
      boxShadow: '0 4px 20px rgba(0,0,0,0.12)', padding: '16px',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '12px' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#3E3D3D' }}>Filtros</Typography>
        <Typography
          onClick={() => setFilters({})}
          sx={{ fontSize: '12px', color: '#006494', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          Limpar
        </Typography>
      </Box>

      <FilterSelect label="Empresa" field="company" options={companies} searchable />
      <FilterSelect label="Cidade" field="city" options={cities} searchable />
      <FilterSelect label="Cargo" field="position" options={positions} searchable />
      <FilterSelect label="Tipo de concessão" field="concession_type" options={concessionTypes} />

      <Box sx={{ borderTop: '1px solid #f0f0f0', pt: '10px', mt: '4px' }}>
        <Toggle label="Somente Premium" field="premium" />
        <Toggle label="Somente Bloqueados" field="blocked" />
      </Box>
    </Box>
  );
};

const UserList = () => {
  const [states, setStates] = useState({
    loading: true, users: [], user: {},
    showModal: false, showUserModal: false, showNewUserModal: false,
  });
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useRef(null);

  const { setShowError, setShowSuccess, setTextSuccessAlert, setTextErrorAlert, textSuccessAlert, textErrorAlert } = useAlert();

  useEffect(() => {
    getUsers(1).then(response =>
      setStates(prev => ({ ...prev, users: response.data, loading: false }))
    );
  }, []);

  useEffect(() => {
    const handleClick = e => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilters(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleTabChange = (_, v) => { setTab(v); setPage(1); };
  const handleSearchChange = e => { setSearch(e.target.value); setPage(1); };

  const openEdit = user => setStates(prev => ({ ...prev, user, showUserModal: true }));

  const handleDelete = () => {
    deleteUser(states.user.id).then(
      () => {
        setShowSuccess(true);
        setTextSuccessAlert('Usuário deletado com sucesso!');
        setStates(prev => ({ ...prev, showModal: false }));
        setTimeout(() => window.location.reload(), 1400);
      },
      () => {
        setShowError(true);
        setTextErrorAlert('Um erro ocorreu ao deletar o usuário.');
        setStates(prev => ({ ...prev, showModal: false }));
      }
    );
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== '' && v !== false && v !== undefined).length;

  const filtered = states.users.filter(u => {
    if (search && !u.name?.toLowerCase().includes(search.toLowerCase()) && !u.email?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filters.company && u.company !== filters.company) return false;
    if (filters.city && u.city !== filters.city) return false;
    if (filters.position && u.position !== filters.position) return false;
    if (filters.concession_type && u.concession_type !== filters.concession_type) return false;
    if (filters.premium && !u.premium) return false;
    if (filters.blocked && !u.blocked) return false;
    return true;
  });

  const admins = filtered.filter(u => u.is_admin);
  const commons = filtered.filter(u => !u.is_admin);
  const listForTab = tab === 0 ? admins : commons;
  const totalPages = Math.ceil(listForTab.length / PAGE_SIZE);
  const listToShow = listForTab.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const total = states.users.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header loggedIn={true} />

      {/* Barra título + busca */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginTop: '80px', marginBottom: '16px', ...paddingSx }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography variant="h1" sx={{ fontWeight: 500, fontSize: '28px', lineHeight: '40px', color: '#3E3D3D' }}>
            Usuários
          </Typography>
          {!states.loading && total > 0 && (
            <Chip label={total} size="small" sx={{ backgroundColor: '#006494', color: '#fff', fontWeight: 700, fontSize: '13px' }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Box sx={{ position: 'relative', width: '220px', maxWidth: '60vw' }}>
            <input
              value={search}
              onChange={handleSearchChange}
              placeholder="Buscar usuários..."
              style={{ width: '100%', padding: '8px 44px 8px 14px', background: '#fff', border: '1px solid rgba(0,0,0,0.18)', borderRadius: '8px', fontSize: '14px', color: '#3E3D3D', outline: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
            />
            <IconButton sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', color: '#9e9e9e' }}>
              <SearchOutlinedIcon />
            </IconButton>
          </Box>

          {/* Botão filtros */}
          <Box sx={{ position: 'relative' }} ref={filterRef}>
            <IconButton
              onClick={() => setShowFilters(v => !v)}
              sx={{
                borderRadius: '8px', border: '1px solid',
                borderColor: activeFilterCount > 0 ? '#006494' : 'rgba(0,0,0,0.18)',
                backgroundColor: activeFilterCount > 0 ? '#e8f4fb' : '#fff',
                color: activeFilterCount > 0 ? '#006494' : '#555',
                '&:hover': { backgroundColor: '#f0f8ff' },
              }}
            >
              <FilterListIcon />
              {activeFilterCount > 0 && (
                <Box sx={{ position: 'absolute', top: '-4px', right: '-4px', width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#006494', color: '#fff', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {activeFilterCount}
                </Box>
              )}
            </IconButton>
            {showFilters && (
              <FilterPanel
                users={states.users}
                filters={filters}
                setFilters={f => { setFilters(f); setPage(1); }}
                onClose={() => setShowFilters(false)}
              />
            )}
          </Box>

          <IconButton
            onClick={() => setStates(prev => ({ ...prev, showNewUserModal: true }))}
            sx={{ color: '#fff', backgroundColor: '#006494', borderRadius: '8px', '&:hover': { backgroundColor: '#004f75' } }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ ...paddingSx, borderBottom: '1px solid #e0e0e0', mb: '16px' }}>
        <Tabs value={tab} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
          <Tab label={`Admin (${admins.length})`} />
          <Tab label={`Comum (${commons.length})`} />
        </Tabs>
      </Box>

      {/* Lista */}
      <Box sx={{ flexGrow: 1, ...paddingSx }}>
        {states.loading ? (
          <Loading />
        ) : listToShow.length > 0 ? (
          <>
            {listToShow.map(user => (
              <UserRow key={user.id} user={user} onClick={openEdit} />
            ))}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" shape="rounded" />
              </Box>
            )}
          </>
        ) : (
          <Typography sx={{ color: '#9e9e9e', mt: '24px' }}>Nenhum usuário encontrado.</Typography>
        )}
      </Box>

      <Footer />

      <DeleteConfirmModal showModal={states.showModal} setStates={setStates} deleteCard={handleDelete} title={states.user?.name} />
      <EditUserModal showUserModal={states.showUserModal} setStates={setStates} user={states.user} onDelete={() => setStates(prev => ({ ...prev, showUserModal: false, showModal: true }))} />
      <NewUserModal showUserModal={states.showNewUserModal} setStates={setStates} />
      <AlertError textAlert={textErrorAlert} />
      <AlertSuccess textAlert={textSuccessAlert} />
    </Box>
  );
};

export default UserList;
